import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import { toast } from "sonner";
import { MagnifyingGlass, DownloadSimple, Flask, User } from "@phosphor-icons/react";

const csvEscape = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v).replace(/"/g, '""');
  return /[",\n]/.test(s) ? `"${s}"` : s;
};

const initials = (name) =>
  (name || "?").split(/\s+/).map(w => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [hideTest, setHideTest] = useState(true);
  const [sortBy, setSortBy] = useState("recent"); // recent | ltv | orders

  useEffect(() => {
    setLoading(true);
    api.get("/admin/customers")
      .then(r => setCustomers(r.data))
      .catch(() => toast.error("Failed to load customers"))
      .finally(() => setLoading(false));
  }, []);

  const totals = useMemo(() => {
    const real = customers.filter(c => !c.is_test);
    return {
      total: customers.length,
      real: real.length,
      test: customers.length - real.length,
      real_with_orders: real.filter(c => c.orders_count > 0).length,
      real_ltv: real.reduce((s, c) => s + (c.ltv || 0), 0),
    };
  }, [customers]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let list = customers.filter(c => (hideTest ? !c.is_test : true));
    if (needle) {
      list = list.filter(c =>
        (c.email || "").toLowerCase().includes(needle) ||
        (c.name || "").toLowerCase().includes(needle) ||
        (c.company || "").toLowerCase().includes(needle)
      );
    }
    if (sortBy === "ltv") list = [...list].sort((a, b) => (b.ltv || 0) - (a.ltv || 0));
    else if (sortBy === "orders") list = [...list].sort((a, b) => (b.orders_count || 0) - (a.orders_count || 0));
    else list = [...list].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return list;
  }, [customers, q, hideTest, sortBy]);

  const exportCSV = () => {
    const rows = [
      ["Email", "Name", "Company", "Signup date", "Briefs", "Orders", "LTV (INR)", "Last order", "Test account"],
      ...filtered.map(c => [
        c.email, c.name, c.company, c.created_at, c.briefs_count, c.orders_count,
        c.ltv, c.last_order_at, c.is_test ? "yes" : "no",
      ]),
    ];
    const csv = rows.map(r => r.map(csvEscape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `talaio-customers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} customers`);
  };

  return (
    <div className="min-h-screen bg-[var(--pf-bg)]">
      <Nav />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="pf-label mb-3">Admin · Customers</div>
            <h1 className="text-4xl sm:text-5xl font-black tracking-tighter">Who&apos;s buying.</h1>
          </div>
          <button
            onClick={exportCSV}
            disabled={filtered.length === 0}
            data-testid="admin-customers-export-csv"
            className="pf-btn-secondary inline-flex items-center gap-2 disabled:opacity-40"
          >
            <DownloadSimple size={16} /> Export CSV
          </button>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="border border-[var(--pf-border)] bg-white p-4">
            <div className="pf-label">Real customers</div>
            <div className="text-3xl font-black tracking-tighter mt-1" data-testid="kpi-real-customers">{totals.real}</div>
          </div>
          <div className="border border-[var(--pf-border)] bg-white p-4">
            <div className="pf-label">Test / QA accounts</div>
            <div className="text-3xl font-black tracking-tighter mt-1 text-[var(--pf-text-3)]">{totals.test}</div>
          </div>
          <div className="border border-[var(--pf-border)] bg-white p-4">
            <div className="pf-label">Real w/ orders</div>
            <div className="text-3xl font-black tracking-tighter mt-1">{totals.real_with_orders}</div>
          </div>
          <div className="border border-[var(--pf-border)] bg-white p-4">
            <div className="pf-label">Real LTV</div>
            <div className="text-3xl font-black tracking-tighter mt-1">₹{totals.real_ltv.toLocaleString("en-IN")}</div>
          </div>
        </div>

        {/* Controls */}
        <div className="flex gap-3 items-center flex-wrap mb-5">
          <div className="relative flex-1 min-w-[260px] max-w-xl">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--pf-text-3)]" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by email, name, company…"
              data-testid="admin-customers-search"
              className="pf-input pl-10 w-full"
            />
          </div>
          <label className="inline-flex items-center gap-2 text-sm font-semibold cursor-pointer select-none" data-testid="toggle-hide-test">
            <input type="checkbox" checked={hideTest} onChange={(e) => setHideTest(e.target.checked)} className="w-4 h-4" />
            Hide test accounts
          </label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            data-testid="admin-customers-sort"
            className="border-2 border-[var(--pf-border)] px-3 py-2 text-xs uppercase tracking-widest font-bold bg-white"
          >
            <option value="recent">Sort · Newest first</option>
            <option value="ltv">Sort · Highest LTV</option>
            <option value="orders">Sort · Most orders</option>
          </select>
        </div>

        {loading ? (
          <div className="border border-dashed border-[var(--pf-border)] p-16 text-center text-[var(--pf-text-2)]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="border border-dashed border-[var(--pf-border)] p-16 text-center text-[var(--pf-text-2)]" data-testid="no-admin-customers">
            {customers.length === 0 ? "No customers yet." : "No customers match this filter."}
          </div>
        ) : (
          <div className="border border-[var(--pf-border)] bg-white">
            {filtered.map(c => (
              <div key={c.id} className="grid grid-cols-12 gap-4 p-4 border-b last:border-0 border-[var(--pf-border)] items-center hover:bg-[var(--pf-bg)] transition-colors" data-testid={`admin-customer-${c.id}`}>
                <div className="col-span-12 sm:col-span-4 flex items-center gap-3 min-w-0">
                  <div className="w-11 h-11 rounded-full bg-[var(--pf-text)] text-white flex items-center justify-center font-black text-sm shrink-0">
                    {initials(c.name)}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="font-semibold text-sm truncate" data-testid={`customer-name-${c.id}`}>{c.name || "—"}</div>
                      {c.is_test && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded shrink-0" data-testid={`badge-test-${c.id}`}>
                          <Flask size={10} weight="fill" /> Test
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[var(--pf-text-3)] truncate">{c.email}</div>
                    {c.company && <div className="text-xs text-[var(--pf-text-3)] truncate">{c.company}</div>}
                  </div>
                </div>

                <div className="col-span-4 sm:col-span-2">
                  <div className="pf-label">Signed up</div>
                  <div className="text-sm font-semibold">{c.created_at ? new Date(c.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</div>
                </div>

                <div className="col-span-4 sm:col-span-2">
                  <div className="pf-label">Briefs · Orders</div>
                  <div className="text-sm font-mono font-bold">{c.briefs_count} · {c.orders_count}</div>
                </div>

                <div className="col-span-4 sm:col-span-2">
                  <div className="pf-label">LTV</div>
                  <div className="text-sm font-mono font-bold">₹{(c.ltv || 0).toLocaleString("en-IN")}</div>
                </div>

                <div className="col-span-12 sm:col-span-2 text-right">
                  <Link
                    to={`/admin/orders?q=${encodeURIComponent(c.email)}`}
                    data-testid={`view-orders-${c.id}`}
                    className={`text-xs font-bold uppercase tracking-widest ${c.orders_count > 0 ? "text-[var(--pf-brand)] hover:underline" : "text-[var(--pf-text-3)] pointer-events-none"}`}
                  >
                    {c.orders_count > 0 ? "View orders →" : "No orders"}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="text-xs text-[var(--pf-text-3)] mt-3">
          Showing {filtered.length} of {customers.length} customers · {totals.test} auto-flagged as test.
          <span className="ml-2">
            <User size={12} className="inline mb-0.5" /> Test detection is heuristic (email patterns like <code>test_</code>, <code>@example.com</code>, <code>@printforge.ai</code>, etc.).
          </span>
        </div>
      </div>
    </div>
  );
}
